import { Injectable } from '@angular/core';
import { ParameterMergedSource, ParameterMergedResult, ParameterMergedValueSources } from './troubleshooting.datamodel';

@Injectable()
export class ParametersOverrideService {
  private static readonly SITE_PREFIX = 'SITE_';

  /**
   * Computes the additional parameter name based on the original parameter name.
   * If the original name starts with SITE_, it removes that prefix; otherwise, it adds SITE_ as a prefix.
   * @param originalParamName - The original parameter name to be processed.
   * @returns The computed additional parameter name.
   */
  private static computeAdditionalParamName(originalParamName: string): string {
    if (originalParamName.startsWith(ParametersOverrideService.SITE_PREFIX)) {
      return originalParamName.substring(ParametersOverrideService.SITE_PREFIX.length);
    } else {
      return ParametersOverrideService.SITE_PREFIX + originalParamName;
    }
  }

  /**
   * Merges the overrides from different levels, resolving conflicts and returning a structured result.
   * @param parametersMapPerLevels - An array of maps where each map contains parameter overrides for a specific level.
   * @returns A structured result containing overrides and conflicts.
   */
  mergeOverrideParameters(
    parametersMapPerLevels: Array<Map<string, Map<string, Array<ParameterMergedSource>>>>): ParameterMergedResult {
    const conflicts: Map<string, Set<ParameterMergedSource>> = new Map();
    const overrides: Map<string, Map<string, Array<ParameterMergedSource>>> = new Map();
    if (parametersMapPerLevels?.length > 0) {
      for (const singleLevelOverrides of parametersMapPerLevels) {
        this.extractConflictsAndOverridesAtSingleLevel(singleLevelOverrides, conflicts, overrides);
      }
    }
    return {overrides: this.formatOverrides(overrides), conflicts};
  }

  /**
   * Extracts conflicts and overrides from a single level of parameter overrides.
   * @param singleLevelOverrides - A map containing parameter overrides for a single level.
   * @param conflicts - A map to store conflicts found during extraction.
   * @param overrides - A map to store valid overrides found during extraction.
   * @returns An object containing the updated conflicts and overrides maps.
   */
  private extractConflictsAndOverridesAtSingleLevel(
    singleLevelOverrides: Map<string, Map<string, Array<ParameterMergedSource>>>,
    conflicts: Map<string, Set<ParameterMergedSource>>,
    overrides: Map<string, Map<string, Array<ParameterMergedSource>>>
  ): void {
    for (const param of singleLevelOverrides.keys()) {
      const singleParamOverrides = this.getOverridesForParam(singleLevelOverrides, param);
      if (this.isInConflict(singleParamOverrides, param)) {
        this.updateConflictsMap(singleParamOverrides, conflicts);
      } else {
        for (const [key, value] of singleParamOverrides.entries()) {
          overrides.set(key, value);
        }
      }
    }
  }

  /**
   * Checks if the given parameter overrides are in conflict.
   * A conflict occurs if there are multiple values for the same parameter or if the site-specific parameter has different values.
   * @param singleParamOverrides - A map containing overrides for a single parameter.
   * @param param - The name of the parameter being checked.
   * @returns True if there is a conflict, false otherwise.
   */
  private isInConflict(
    singleParamOverrides: Map<string, Map<string, Array<ParameterMergedSource>>>,
    param: string
  ): boolean {
    const paramAndSiteParamOverridesWithDifferentValues =
      singleParamOverrides.size > 1 &&
      !singleParamOverrides
      .get(ParametersOverrideService.computeAdditionalParamName(param))
      ?.has([...singleParamOverrides.get(param)?.keys() || []][0]);

    const paramOverridesWithDifferentValues = (singleParamOverrides.get(param)?.size ?? 0) > 1;

    return paramAndSiteParamOverridesWithDifferentValues || paramOverridesWithDifferentValues;
  }

  /**
   * Updates the conflicts map with the overrides found for a specific parameter.
   * @param singleParameterOverrides - A map containing overrides for a single parameter.
   * @param conflicts - The map to be updated with conflicts.
   */
  private updateConflictsMap(
    singleParameterOverrides: Map<string, Map<string, Array<ParameterMergedSource>>>,
    conflicts: Map<string, Set<ParameterMergedSource>>
  ): void {
    for (const [paramName, overrides] of singleParameterOverrides.entries()) {
      if (!conflicts.has(paramName)) {
        conflicts.set(paramName, new Set());
      }
      for (const overrideList of overrides.values()) {
        for (const singleOverride of overrideList) {
          conflicts.get(paramName)?.add(singleOverride);
        }
      }
    }
  }

  /**
   * Retrieves the overrides for a specific parameter, including its site-specific counterpart if applicable.
   * @param singleLevelOverrides - A map containing parameter overrides for a single level.
   * @param paramName - The name of the parameter for which overrides are being retrieved.
   * @returns A map containing the overrides for the specified parameter and its site-specific counterpart.
   */
  private getOverridesForParam(
    singleLevelOverrides: Map<string, Map<string, Array<ParameterMergedSource>>>,
    paramName: string
  ): Map<string, Map<string, Array<ParameterMergedSource>>> {
    const totalOverridesForParam = new Map<string, Map<string, Array<ParameterMergedSource>>>();
    totalOverridesForParam.set(paramName, singleLevelOverrides.get(paramName) || new Map());

    const additionalParamName = ParametersOverrideService.computeAdditionalParamName(paramName);
    const additionalOverrides = singleLevelOverrides.get(additionalParamName);
    if (additionalOverrides) {
      totalOverridesForParam.set(additionalParamName, additionalOverrides);
    }

    return totalOverridesForParam;
  }

  /**
   * Formats the valid overrides into a map where each key is a parameter name and the value is its corresponding override.
   * @param validOverrides - A map containing valid overrides for parameters.
   * @returns A formatted map with parameter names as keys and their overrides as values.
   */
  private formatOverrides(
    validOverrides: Map<string, Map<string, Array<ParameterMergedSource>>>
  ): Map<string, ParameterMergedValueSources> {
    const outputMap = new Map<string, ParameterMergedValueSources>();
    for (const [paramName, paramValues] of validOverrides.entries()) {
      const [paramValueKey] = [...paramValues.entries()][0];
      const otherParamName = ParametersOverrideService.computeAdditionalParamName(paramName);
      const sources = (paramValues.get(paramValueKey) || []).concat(validOverrides.get(otherParamName)?.get(paramValueKey) || []);
      outputMap.set(paramName, { parameterValue: paramValueKey, sources });
      outputMap.set(otherParamName, { parameterValue: paramValueKey, sources });
    }
    return outputMap;
  }
}
